<template>
  <VRow>
    <VCol cols="12">
      <VCard title="Master Data Tags">
        <VRow class="mb-4">
          <VCol>
            <VCardText>Manage your tags data efficiently using the tools below.</VCardText>
          </VCol>
          <VCol cols="auto">
            <VBtn
              @click="openPopup"
              size="small"
              class="mr-4"
            >
              Add New
              <VIcon
                class="ml-1"
                icon="ri-add-large-line"
              />
            </VBtn>
          </VCol>
        </VRow>

        <VTable>
          <thead>
            <tr>
              <th>No</th>
              <th class="text-uppercase text-center">Tag Name</th>
              <th class="text-uppercase text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(tag, index) in tags"
              :key="tag.id"
            >
              <td>{{ index + 1 }}</td>
              <td class="text-center">{{ tag.name }}</td>
              <td class="text-center">
                <VBtn
                  color="error"
                  size="small"
                  @click="openDeleteConfirmation(tag.id)"
                >
                  Delete
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCard>
    </VCol>
  </VRow>

  <!-- Popup for adding new tag -->
  <v-dialog
    max-width="500"
    v-model="isPopupActive"
  >
    <v-card title="Input Master Data Tags">
      <v-card-text>
        <form @submit.prevent="handleSubmit">
          <VRow>
            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="tagName">Tag Name</label>
                </VCol>
                <VCol
                  cols="12"
                  md="9"
                >
                  <VTextField
                    id="tagName"
                    type="text"
                    v-model="formData.name"
                    prefix="@"
                    placeholder="Enter Tag Name"
                    persistent-placeholder
                  />
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                />
                <VCol
                  cols="12"
                  md="9"
                >
                  <VBtn
                    type="submit"
                    class="me-4"
                    >Submit</VBtn
                  >
                  <VBtn
                    @click="closePopup"
                    color="secondary"
                    variant="outlined"
                    >Cancel</VBtn
                  >
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </form>
      </v-card-text>
    </v-card>
  </v-dialog>
  <!-- Confirmation Popup -->
  <v-dialog
    max-width="400"
    v-model="isDeletePopupActive"
  >
    <v-card>
      <v-card-title>Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete this tag?</v-card-text>
      <v-card-actions>
        <VBtn
          color="secondary"
          variant="outlined"
          @click="closeDeletePopup"
        >
          Cancel
        </VBtn>
        <VBtn
          color="error"
          @click="confirmDelete"
        >
          Delete
        </VBtn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Alert Message -->
  <v-snackbar
    v-model="isShowSnackBar"
    multi-line
    :globalTop="true"
    :color="alertType"
    dismissible
    class="mb-4"
    @click:close="clearAlert"
  >
    {{ alertMessage }}
  </v-snackbar>
</template>
<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { usePopUp } from '@/composables/usePopUp'
import { useFetch } from '@vueuse/core'
import { onMounted, ref } from 'vue'
const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()
const { isPopupActive, openPopup, closePopup } = usePopUp()

// Reactive state
const formData = ref({ name: '' })
const isDeletePopupActive = ref(false)
const selectedTagId = ref<number | null>(null)
const tags = ref<Tag[]>([])

// Interfaces
interface Tag {
  id: number
  name: string
}

// Fetch tags
const fetchTags = async () => {
  try {
    const { data } = await useFetch('/api/tags')
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to fetch tags')

    tags.value = parsedData.data || []
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while fetching tags.', 'error')
  }
}

// Open delete confirmation popup
const openDeleteConfirmation = (id: number) => {
  selectedTagId.value = id
  isDeletePopupActive.value = true
}

// Close delete confirmation popup
const closeDeletePopup = () => {
  selectedTagId.value = null
  isDeletePopupActive.value = false
}

// Handle form submission
const handleSubmit = async () => {
  if (!formData.value.name) {
    showAlert('Please fill in all required fields.', 'error')
    return
  }

  try {
    const newTag = { name: formData.value.name } // Removed id from the payload

    const { data } = await useFetch('/api/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTag),
    })
    const parsedData = JSON.parse(data.value as string)

    const { message, success } = (parsedData as { message: string; success: boolean }) || {}

    if (!success) throw new Error(message || 'Failed to add tag')

    resetForm()
    showAlert(message || 'Tag added successfully', 'success')
    closePopup()
    await fetchTags()
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while adding the tag.', 'error')
  }
}

// Confirm delete
const confirmDelete = async () => {
  if (selectedTagId.value === null) return

  try {
    const { data } = await useFetch(`/api/tags?id=${selectedTagId.value}`, {
      method: 'DELETE',
    })
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to delete tag')

    showAlert(parsedData.message || 'Tag deleted successfully', 'success')
    await fetchTags()
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while deleting the tag.', 'error')
  } finally {
    closeDeletePopup()
  }
}

// Utility functions

const resetForm = () => {
  formData.value.name = '' // Removed id reset
}

// Fetch tags on mount
onMounted(fetchTags)
</script>
